import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { ApiService } from 'src/app/services/api/api.service';

import { ErrorModalComponent } from 'src/app/shared/modals/error-modal/error-modal.component';
import { SuccessModalComponent } from 'src/app/shared/modals/success-modal/success-modal.component';
import { UtilitiesService } from 'src/app/services/utilities/utilities.service';
import { Router } from '@angular/router';
import {
  handleFileUploaded,
  handleFileUploadError,
} from 'src/app/shared/components/upload-input/file-upload-helper';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ConfirmActionComponent } from 'src/app/shared/components/confirm-action/confirm-action.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-birth-registration-application',
  standalone: false,
  templateUrl: './birth-registration-application.component.html',
  styleUrl: './birth-registration-application.component.scss'
})
export class BirthRegistrationApplicationComponent {
  isSubmitting: boolean;
  isVerifyingAsin: boolean;
  today: string;
  previewed: boolean;
  constructor(
    private formBuilder: FormBuilder,
    private apiService: ApiService,
    private dialog: MatDialog,
    private utilitiesService: UtilitiesService,
    private router: Router,
    private authService: AuthService
  ) { }

  showErrorMessages: boolean = false;

  form: FormGroup;

  streetTypes: any[];

  isLoading: boolean;

  ngOnInit(): void {

    this.today = new Date().toISOString().split('T')[0];

    this.initializeForm();
  }


  initializeForm() {
    this.form = this.formBuilder.group({
      surname: ['', [Validators.required]],
      firstName: ['', [Validators.required]],
      middleName: [''],
      dob: ['', [Validators.required]],
      gender: ['', [Validators.required]],
      orderOfBirth: ['', [Validators.required]],
      typeOfBirth: ['', [Validators.required]],
      placeOfBirth: ['', [Validators.required]],
      timeOfBirth : [''],


      bloodGroup : [''],
      genoType : [''],
      weight: [
        null,
        [
          Validators.min(1),
          Validators.max(100),
          Validators.pattern(/^\d+(\.\d{1,2})?$/)
        ]
      ],
      religion : [''],
      markAtBirth : [''],

      fatherAsin: [''],
      fatherSurname: ['', [Validators.required]],
      fatherFirstName: ['', [Validators.required]],
      fatherMiddleName: [''],
      fatherPhoneNumber: ['', [Validators.required, this.phoneNumberValidator() as ValidatorFn]],
      fatherAddress: ['', [Validators.required]],
      fatherMaritalStatus: ['', [Validators.required]],
      fatherOccupation: ['', [Validators.required]],

      motherAsin: [''],
      motherSurname: ['', [Validators.required]],
      motherFirstName: ['', [Validators.required]],
      motherMiddleName: [''],
      motherPhoneNumber: ['', [Validators.required, this.phoneNumberValidator() as ValidatorFn]],
      motherAddress: ['', [Validators.required]],
      motherMaritalStatus: ['', [Validators.required]],
      motherOccupation: ['', [Validators.required]],

    });
  }

  phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value === null || control.value === undefined) {
        return null;
      }

      const cleanedPhoneNumber = control.value
        .replace(/\s/g, '')
        .replace(/-/g, '');

      const isDigitsOnly = /^\d+$/.test(cleanedPhoneNumber);

      if (
        cleanedPhoneNumber.length === 0 ||
        (cleanedPhoneNumber.length === 11 && isDigitsOnly) ||
        (cleanedPhoneNumber.length === 13 &&
          cleanedPhoneNumber.startsWith('234')) ||
        (cleanedPhoneNumber.length === 14 &&
          cleanedPhoneNumber.startsWith('+234'))
      ) {
        return null;
      } else {
        return { invalidPhoneNumber: true };
      }
    };
  }


  onError(errorResponse: any, controlName: string) {
    handleFileUploadError(errorResponse, this.form, controlName);
  }

  verifyMotherAsin() {
    const asin = this.form.get('motherAsin')?.value;

    this.isVerifyingAsin = true;

    this.apiService
      .get('/utility/asin/validate?tin=' + asin)
      .pipe(
        finalize(() => {
          this.isVerifyingAsin = false;
        })
      )
      .subscribe(
        (response: any) => {

          this.form.patchValue({
            motherFirstName: response?.firstName,
            motherSurname: response?.lastName,
            motherPhoneNumber: response?.phoneNumber,
            motherAddress: response?.firstName,
          });

          this.openAsinVerificationSuccessModal('ASIN verified successfuly');
        },
        (error) => {
          this.openErrorModal(error?.message);
        }
      );
  }

  verifyFatherAsin() {
    const asin = this.form.get('fatherAsin')?.value;

    this.isVerifyingAsin = true;

    this.apiService
      .get('/utility/asin/validate?tin=' + asin)
      .pipe(
        finalize(() => {
          this.isVerifyingAsin = false;
        })
      )
      .subscribe(
        (response: any) => {

          this.form.patchValue({
            fatherFirstName: response?.firstName,
            fatherSurname: response?.lastName,
            fatherPhoneNumber: response?.phoneNumber,
            fatherAddress: response?.firstName,
          });

          this.openAsinVerificationSuccessModal('ASIN verified successfuly');
        },
        (error) => {
          this.openErrorModal(error?.message);
        }
      );
  }

  previewForm() {
    this.previewed = true;
    this.form.disable();
  }

  enableEdit() {
    this.previewed = false;
    this.form.enable();
  }


  openSuccessModal(message: string) {
    let dialogRef = this.dialog.open(SuccessModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { successMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.router.navigate(['/admin/birth-registration']);
    });
  }

  openErrorModal(message: string) {
    let dialogRef = this.dialog.open(ErrorModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { errorMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => { });
  }


  openConfirmModal(){

    let dialogRef = this.dialog.open(ConfirmActionComponent, {
      width: '400px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      disableClose: true,

      data: {
        title: "Submit Birth Registration?",
        body: '',
        confirmText: 'Yes',
        lottieIconPath: 'assets/lottie/alert.json',
        onConfirm: () => {

          this.create()

        },
        onCancel: () => {

        },
        showCancelButton: true,
        showConfirmButton: true,
      },
    });

  }


  async create(): Promise<void> {
      this.isSubmitting = true;

      try {
        const formData = this.getFormValues();

        const request = await this.mapFormValuesToNestedObject(formData);

        const url = '/birth/create';

        this.apiService
          .post(url, request)
          .pipe(finalize(() => {
            this.isSubmitting = false;
          }))
          .subscribe(
            (res) => {
              if (res.success) {

                this.openSuccessModal('Birth Registration submitted successfully');

              } else {
                this.openErrorModal(res.error || res.message);
              }
            },
            (error) => {
              this.openErrorModal(error?.message);
            }
          );
      } catch (error) {
        this.isSubmitting = false;
        this.openErrorModal('Failed to process form data');
      }

  }

  hasAccess(permissions: string[]) {
    return this.authService.hasAnyPermission(permissions)
  }

  getFormValues() {
    const formData = {};
    Object.keys(this.form.controls).forEach(control => {
      formData[control] = this.form.get(control)?.value;
    });
    return formData;
  }


  async mapFormValuesToNestedObject(formValue: any): Promise<any> {
    return {
      firstsName: formValue.firstName,
      lastName: formValue.surname,
      middleName: formValue.middleName,
      dob: formValue.dob,
      gender: formValue.gender,
      orderOfBirth: formValue.orderOfBirth,
      typeOfDelivery: formValue.typeOfBirth,
      placeOfBirth: formValue.placeOfBirth,
      timeOfBirth: formValue.timeOfBirth,
      bloodGroup: formValue.bloodGroup,
      genoType: formValue.genoType,
      weight: formValue.weight,
      religion: formValue.religion,
      markAtBirth: formValue.markAtBirth,
      parents: [
        {
          asin: formValue.fatherAsin,
          firstName: formValue.fatherFirstName,
          lastName: formValue.fatherSurname,
          otherName: formValue.fatherMiddleName,
          phoneNumber: formValue.fatherPhoneNumber,
          address: formValue.fatherAddress,
          parentType: 'Father',
          maritalStatus: formValue.fatherMaritalStatus,
          occupation: formValue.fatherOccupation
        },
        {
          asin: formValue.motherAsin,
          firstName: formValue.motherFirstName,
          lastName: formValue.motherSurname,
          otherName: formValue.motherMiddleName,
          phoneNumber: formValue.motherPhoneNumber,
          address: formValue.motherAddress,
          parentType: 'Mother',
          maritalStatus: formValue.motherMaritalStatus,
          occupation: formValue.motherOccupation
        }
      ]
    };
  }


  openAsinVerificationSuccessModal(message: string) {
    let dialogRef = this.dialog.open(SuccessModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { successMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => { });
  }

  back() {
    window.history.back()
  }

  startInterswitch(res: any) {

    const url = '/payment/initialize';
    const details = {
      paymentAmount: 5200,
      callbackUrl: environment.envUrl + 'admin/birth-registration',
      email: '',
      phoneNumer: this.form.get("fatherPhoneNumber")?.value,
      asin: this.form.get("fatherAsin")?.value,
      revenueName: "COURT WEDDING/ CERTIFICATE",
      entityId: res?.data?.id,
      entityType: "BIRTH_REGISTRATION"
    };
    this.apiService.post(url, details).subscribe({
      next: (response) => {
        this.checkout(response.data);
      },
      error: (error) => {
        console.info(`Unable to reach Interswitch: ${error}`);
      },
    });
  }

  checkout(details: any) {
    var paymentRequest = {
      merchant_code: details?.merchantCode,
      pay_item_id: details?.payItemId,
      txn_ref: details?.orderId,
      cust_id: details?.customerId,
      amount: Math.round((details?.amount * 100 + Number.EPSILON) * 100) / 100,
      currency: '566',
      site_redirect_url: window.location.origin,
      onComplete: (response: any) => this.paymentCallback(response),
      mode: details?.mode,
    };

    if (
      details?.mode === 'LIVE' &&
      details?.splitAccounts &&
      Object.keys(details?.splitAccounts).length > 0
    ) {
      paymentRequest['split_accounts'] = JSON.stringify(details?.splitAccounts);
    }

    (window as any).webpayCheckout(paymentRequest);
  }

  paymentCallback(response: any) {

    const url = '/payment/acknowledge?orderId=' + response?.txnref;
    this.apiService.post(url, {}).subscribe(
      (response) => {
        this.router.navigate(['/admin/birth-registration']);
      },
      (error) => {
        console.info(`Unable to reach Interswitch: ${error}`);
      }
    );
    return;
  }

}
